import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { AccountType } from '@mews-commander/data-access';
import { RouteName } from '@mews-commander/enums';
import { LEGAL_ENTITY_IDENTIFIERS } from '@mews-commander/legacy-form';
import { AsyncAction, CommanderThunkAction } from '@mews-commander/types';
import { createAction, lt, showSuccessAlert } from '@mews/framework';
import { createFetchAction, createUpdateValue, notEmpty } from '@mews-commander/utils';
import { getCompany } from '@mews-commander/legacy-entities/src/ducks';
import { updateAccount, deleteAccount } from '@mews-commander/legacy-entities/src/actions';
import { navigateInModal, refreshPage } from '../../app/actions/common';
import {
    COMPANY_DELETE,
    COMPANY_DETAIL_CONTACT_INFO_UPDATE_FAILED,
    COMPANY_DETAIL_CONTACT_INFO_UPDATE_STARTED,
    COMPANY_DETAIL_CONTACT_INFO_UPDATE_SUCCEEDED,
} from '../actionTypes';
import { ContactFormValues } from '../types';

export const updateContactInfo =
    (values: ContactFormValues, companyId: string): CommanderThunkAction =>
    async (dispatch, getState) => {
        dispatch(createAction(COMPANY_DETAIL_CONTACT_INFO_UPDATE_STARTED));
        const current = getCompany(getState(), { id: companyId });
        if (!current) {
            dispatch(createAction(COMPANY_DETAIL_CONTACT_INFO_UPDATE_FAILED));
            return;
        }

        let legalEntityIdentifiers;
        if (notEmpty(values[LEGAL_ENTITY_IDENTIFIERS])) {
            legalEntityIdentifiers = pipe(
                values[LEGAL_ENTITY_IDENTIFIERS],
                array.reduce({} as Record<string, { value: string | null }>, (result, item) => {
                    const [key, value] = Object.entries(item)[0];
                    return { ...result, [key]: { value } };
                })
            );
        }
        const accountUpdates = [
            {
                data: {
                    discriminator: AccountType.Company,
                    value: {
                        id: companyId,
                        name: createUpdateValue(current.name, values.name),
                        motherCompanyId: createUpdateValue(current.motherCompanyId, values.motherCompanyId),
                        email: createUpdateValue(current.email, values.email),
                        department: createUpdateValue(current.department, values.department),
                        websiteUrl: createUpdateValue(current.website, values.websiteUrl),
                        referenceId: createUpdateValue(current.referenceId, values.referenceId),
                        telephone: createUpdateValue(current.telephone, values.telephone),
                        notes: createUpdateValue(current.notes, values.notes),
                        fiscalIdentifier: createUpdateValue(current.fiscalIdentifier, values.fiscalIdentifier),
                        accountingCode: createUpdateValue(current.accountingCode, values.accountingCode),
                        taxIdentifier: createUpdateValue(current.taxIdentifier, values.taxIdentifier),
                        additionalTaxIdentifier: createUpdateValue(
                            current.additionalTaxIdentifier,
                            values.additionalTaxIdentifier
                        ),
                        billingCode: createUpdateValue(current.billingCode, values.billingCode),
                        dunsNumber: createUpdateValue(current.dunsNumber, values.dunsNumber),
                        externalIdentifier: createUpdateValue(current.externalIdentifier, values.externalIdentifier),
                        legalEntityIdentifiers,
                    },
                },
            },
        ];
        await dispatch(updateAccount({ accountUpdates })).then(() =>
            showSuccessAlert({
                description: lt('SaveSuccessMessage'),
            })
        );
        return dispatch(createAction(COMPANY_DETAIL_CONTACT_INFO_UPDATE_SUCCEEDED));
    };

export const deleteCompany =
    (accountId: string): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { resolveRoute }) => {
        const promise = dispatch(deleteAccount({ accountId }))
            .then(() => {
                showSuccessAlert({
                    description: lt('AccountDeleteSuccessMessage'),
                });
                return dispatch(navigateInModal(resolveRoute(RouteName.CompanyReportIndex)));
            })

            .catch(() => dispatch(refreshPage()));
        return dispatch(createFetchAction(COMPANY_DELETE, promise));
    };
